/**
 * Change Tracking - Diff Engine
 *
 * Computes differences between two reports for highlighting changes.
 */

import type { Report, ValueHighlight } from '@/data/schemas';

/**
 * Computes highlights by comparing two report versions.
 *
 * This function performs a deep diff of report.data.values using keypaths as the
 * comparison key. It detects three types of changes:
 * - **Modified**: Value exists in both reports but has changed
 * - **Added**: Value exists in current report but not in previous
 * - **Removed**: Value exists in previous report but not in current
 *
 * **Algorithm**: O(n) where n = total unique keypaths across both reports.
 * Uses deep equality (via lodash isEqual) to compare complex values.
 *
 * **Use Cases**:
 * - Quarterly audit reports: Show what changed since last quarter
 * - Compliance tracking: Highlight safety status changes
 * - Equipment monitoring: Track when units are added/modified/removed
 *
 * @param options.currentReport - The newer report version
 * @param options.previousReport - The older report version to compare against
 * @param options.onlyModifications - If true, only track "modified" (not "added"/"removed")
 * @returns Highlights object and statistics about changes
 *
 * @example
 * ```typescript
 * // Basic usage
 * const current = await ReportsRepo.get(currentId);
 * const previous = await ReportsRepo.get(current.previousReportId!);
 *
 * const { highlights, stats } = computeHighlights({
 *   currentReport: current,
 *   previousReport: previous,
 * });
 *
 * console.log(stats);
 * // { modified: 3, added: 2, removed: 1 }
 *
 * console.log(highlights);
 * // {
 * //   "sec_units.row_0.fld_power": {
 * //     previousValue: 150,
 * //     reason: "modified"  // Changed from 150 to 200
 * //   },
 * //   "sec_units.row_2.fld_name": {
 * //     reason: "added"  // New unit added
 * //   }
 * // }
 *
 * // Store in report for rendering
 * current.data.highlights = highlights;
 * await ReportsRepo.upsert(current);
 * ```
 *
 * @example
 * ```typescript
 * // Only modifications (ignore additions/removals)
 * const { highlights } = computeHighlights({
 *   currentReport: current,
 *   previousReport: previous,
 *   onlyModifications: true,  // ← Focus on what changed
 * });
 *
 * // Only contains "modified" highlights, no "added" or "removed"
 * ```
 */
export function computeHighlights({
  currentReport,
  previousReport,
  onlyModifications = false,
}: {
  currentReport: Report;
  previousReport: Report;
  onlyModifications?: boolean;
}): {
  highlights: Record<string, ValueHighlight>;
  stats: { modified: number; added: number; removed: number };
} {
  const highlights: Record<string, ValueHighlight> = {};
  const stats = { modified: 0, added: 0, removed: 0 };

  const currentValues = currentReport.data.values;
  const previousValues = previousReport.data.values;

  // Find modified and added values
  for (const [keypath, currentValue] of Object.entries(currentValues)) {
    if (keypath in previousValues) {
      // Value exists in both - check if modified
      const previousValue = previousValues[keypath];
      if (!isEqual(currentValue, previousValue)) {
        highlights[keypath] = {
          previousValue,
          reason: 'modified',
        };
        stats.modified++;
      }
    } else if (!onlyModifications) {
      // Value is new in current report
      highlights[keypath] = {
        reason: 'added',
      };
      stats.added++;
    }
  }

  // Find removed values (exist in previous but not in current)
  if (!onlyModifications) {
    for (const keypath of Object.keys(previousValues)) {
      if (!(keypath in currentValues)) {
        highlights[keypath] = {
          previousValue: previousValues[keypath],
          reason: 'removed',
        };
        stats.removed++;
      }
    }
  }

  return { highlights, stats };
}

/**
 * Deep equality check for values
 */
function isEqual(a: unknown, b: unknown): boolean {
  // Handle null/undefined
  if (a === b) return true;
  if (a == null || b == null) return false;

  // Handle different types
  if (typeof a !== typeof b) return false;

  // Handle primitives
  if (typeof a !== 'object') return a === b;

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  // Handle objects
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;

  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);

  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((key) => isEqual(aObj[key], bObj[key]));
}

/**
 * Merge highlights from multiple sources
 * Later sources override earlier ones
 */
export function mergeHighlights(
  ...highlightMaps: Array<Record<string, ValueHighlight>>
): Record<string, ValueHighlight> {
  const result: Record<string, ValueHighlight> = {};

  for (const map of highlightMaps) {
    Object.assign(result, map);
  }

  return result;
}

/**
 * Filter highlights by keypath pattern
 */
export function filterHighlights(
  highlights: Record<string, ValueHighlight>,
  pattern: string | RegExp
): Record<string, ValueHighlight> {
  const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
  const result: Record<string, ValueHighlight> = {};

  for (const [keypath, highlight] of Object.entries(highlights)) {
    if (regex.test(keypath)) {
      result[keypath] = highlight;
    }
  }

  return result;
}
